import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { readConsent, isNoTagPage, CONSENT_EVENT } from "../lib/consent";
import { persistVisitSource } from "../lib/visitSource";

const GA_ID = process.env.REACT_APP_GA_ID;
const GADS_ID = process.env.REACT_APP_GADS_ID;
const GADS_LABEL = process.env.REACT_APP_GADS_CONVERSION_LABEL;
const GTM_ID = process.env.REACT_APP_GTM_ID;
const CLARITY_ID = process.env.REACT_APP_CLARITY_ID;
const TAG_COOKIES = /^(_ga|_gid|_gat|_gcl|_clck|_clsk|CLID|MUID|_uet)/;

const want = (c) => ({
  google: !!c && ((!!GADS_ID && c.ads) || (!!GA_ID && c.analytics) || (!!GTM_ID && (c.ads || c.analytics))),
  clarity: !!c && !!CLARITY_ID && c.analytics,
});

// Google consent mode v2 - personalised ads always denied for a health service.
const googleConsent = (c) => ({
  ad_storage: c.ads ? "granted" : "denied", ad_user_data: c.ads ? "granted" : "denied",
  ad_personalization: "denied", analytics_storage: c.analytics ? "granted" : "denied",
});

const addScript = (id, src) => {
  const s = document.createElement("script");
  s.id = id; s.async = true; s.src = src;
  document.head.appendChild(s);
};

function loadGoogle(c) {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("consent", "default", { ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", analytics_storage: "denied" });
  window.gtag("consent", "update", googleConsent(c));
  window.gtag("set", "ads_data_redaction", !c.ads);
  if (GA_ID || GADS_ID) addScript("ga-script", `https://www.googletagmanager.com/gtag/js?id=${GA_ID || GADS_ID}`);
  window.gtag("js", new Date());
  if (GA_ID && c.analytics) window.gtag("config", GA_ID, { anonymize_ip: true });
  if (GADS_ID && c.ads) window.gtag("config", GADS_ID);
  if (GTM_ID) {
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    addScript("gtm-script", `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`);
  }
}

function loadClarity() {
  window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
  addScript("clarity-script", `https://www.clarity.ms/tag/${CLARITY_ID}`);
  window.clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "granted" });
}

function clearTagCookies() {
  const host = window.location.hostname;
  const domains = ["", host, `.${host}`, `.${host.split(".").slice(-3).join(".")}`, `.${host.split(".").slice(-2).join(".")}`];
  document.cookie.split(";").map((x) => x.split("=")[0].trim()).filter((n) => TAG_COOKIES.test(n)).forEach((n) =>
    domains.forEach((d) => { document.cookie = `${n}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d ? `; domain=${d}` : ""}`; }));
}

// Count a click on "Book" from a public page as an advert conversion (booking pages themselves carry no tags).
function onBookClick(e) {
  const a = e.target.closest?.("a[href]");
  if (!a || typeof window.gtag !== "function" || !GADS_ID || !readConsent()?.ads) return;
  const path = new URL(a.href, window.location.href).pathname;
  if (!(path.startsWith("/enquiry") || path.startsWith("/follow-up"))) return;
  if (GADS_LABEL) window.gtag("event", "conversion", { send_to: `${GADS_ID}/${GADS_LABEL}`, transport_type: "beacon" });
  window.gtag("event", "begin_booking", { transport_type: "beacon" });
}

export const Tags = () => {
  const { pathname } = useLocation();
  const [consent, setConsent] = useState(readConsent);

  useEffect(() => {
    const f = () => setConsent(readConsent());
    window.addEventListener(CONSENT_EVENT, f);
    document.addEventListener("click", onBookClick, true);
    return () => { window.removeEventListener(CONSENT_EVENT, f); document.removeEventListener("click", onBookClick, true); };
  }, []);

  useEffect(() => {
    persistVisitSource(!!consent?.analytics);
    const loaded = window.__ccpcTags;
    const w = isNoTagPage(pathname) ? { google: false, clarity: false } : want(consent);
    const sig = JSON.stringify([!!consent?.analytics, !!consent?.ads]);
    if (loaded && (loaded.sig !== sig || (loaded.google && !w.google) || (loaded.clarity && !w.clarity))) {
      // Tags are loaded but the choice changed or this page must be tag-free: wipe their cookies if needed and reload clean.
      if (!consent?.analytics || !consent?.ads) clearTagCookies();
      window.location.reload();
      return;
    }
    if (w.google && !loaded?.google) loadGoogle(consent);
    if (w.clarity && !loaded?.clarity) loadClarity();
    if (w.google || w.clarity) window.__ccpcTags = { google: w.google || !!loaded?.google, clarity: w.clarity || !!loaded?.clarity, sig };
  }, [pathname, consent]);

  return null;
};
